<template>
  <div>
    <PageHeader title="Declared Claims">
      <SecondaryButton v-if="isEstalyManager" @click="showExportModal = true">
        {{ t("export_button") }}
      </SecondaryButton>
    </PageHeader>
    <SearchToolbar
      class="mb-4"
      :placeholder="$t('SearchClaims')"
      :available-filters="toolbarAvailableFilters"
      :selected-filters="toolbarSelectedFilters"
      :initial-keywords="keywords"
      @update-filter-search="updateFilterSearch"
      @update-keyword-search="updateKeywordSearch"
    >
      <template #append>
        <SortToggle v-model="order" :options="orderOptions" />
      </template>
    </SearchToolbar>
    <PauseClaimModal v-if="claimToPause" :claim="claimToPause" @confirm="pauseClaim" @close="claimToPause = null" />
    <BulkExportClaimsModal
      :open="showExportModal"
      :selected-filters="selectedFilters"
      :available-filters="availableFilters"
      @close="showExportModal = false"
    />
    <Tabs :tabs="tabs" @tab-selected="selectTab" />
    <div class="mt-4">
      <Loading v-if="loading" />
      <div v-else-if="claims.length === 0" class="text-center mt-4 py-2">
        <p>Aucun sinistre</p>
      </div>
      <PaginatedClaimList
        v-else
        :claims="claims"
        :current-page="currentPage"
        :total-count="totalCount"
        :total-pages="totalPages"
        @changePage="updatePage"
        @pause="(claim) => (claimToPause = claim)"
      />
    </div>
  </div>
</template>

<script>
import PaginatedClaimList from "@/components/claim/list/PaginatedClaimList.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { PageHeader, Loading, Tabs, SearchToolbar, SortToggle, SecondaryButton } from "@estaly/ui";
import PauseClaimModal from "@/components/claim/PauseClaimModal.vue";
import BulkExportClaimsModal from "@/components/claim/BulkExportClaimsModal.vue";
import { useI18n } from "vue-i18n";

export default {
  components: {
    Tabs,
    PageHeader,
    SearchToolbar,
    SortToggle,
    SecondaryButton,
    PaginatedClaimList,
    Loading,
    PauseClaimModal,
    BulkExportClaimsModal,
  },
  props: {
    // Set by the Mes dossiers and Non assignés routes, which replace the assignee filter.
    routeAssignee: { type: String, default: null },
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      currentPage: parseInt(this.$route.query.page) || 1,
      loading: true,
      claimToPause: null,
      showExportModal: false,
      availableFilters: {
        status: [],
        organizations: [],
        stores: [],
        category: [],
        insurerContractGroupReference: [],
        claimType: [],
        assignee: [],
      },
      tabs: [
        {
          name: "open",
          current: false,
          displayName: "Ouvert",
        },
        {
          name: "paused",
          current: false,
          displayName: "En pause",
        },
        {
          name: "closed",
          current: false,
          displayName: "Fermé",
        },
        {
          name: null,
          current: false,
          displayName: "Tous",
        },
      ],
    };
  },
  computed: {
    ...mapState("claims", ["claims", "totalPages", "totalCount", "selectedFilters", "keywords"]),
    ...mapState("stores", { storeRecords: "index" }),
    ...mapState("organizations", { organizationRecords: "index" }),
    ...mapState("estalyManagers", ["claimsHandlers"]),
    ...mapGetters("claims", { statuses: "statuses", claimTypes: "claimTypes" }),
    ...mapGetters("auth", ["isEstalyManager"]),
    order: {
      get() {
        return this.$store.state.claims.order;
      },
      set(value) {
        this.updateOrder(value);
      },
    },
    toolbarAvailableFilters() {
      if (!this.routeAssignee) return this.availableFilters;

      return Object.fromEntries(Object.entries(this.availableFilters).filter(([name]) => name !== "assignee"));
    },
    toolbarSelectedFilters() {
      return this.routeAssignee ? { ...this.selectedFilters, assignee: [] } : this.selectedFilters;
    },
    currentTabName() {
      return this.tabs.find((t) => t.current)?.name ?? null;
    },
    currentTabQueryParam() {
      return this.currentTabName || "all";
    },
    orderOptions() {
      return [
        { label: this.t("newest_first"), value: "desc" },
        { label: this.t("oldest_first"), value: "asc" },
      ];
    },
  },
  watch: {
    routeAssignee() {
      this.refreshFromFirstPage();
    },
    "$route.query.order"(newOrder) {
      this.order = newOrder === "asc" ? "asc" : "desc";
    },
    order() {
      this.refreshFromFirstPage();
    },
    "$route.query.page"(newPage) {
      this.currentPage = parseInt(newPage) || 1;
    },
    currentPage() {
      this.retrieveClaims();
    },
  },
  beforeCreate() {
    // Avant l'enregistrement des watchers : un ordre explicite dans l'URL prime sur celui memorise dans le store.
    const orderFromQuery = this.$route.query.order;
    if (orderFromQuery === "asc" || orderFromQuery === "desc") {
      this.$store.dispatch("claims/updateOrder", orderFromQuery);
    }
  },
  async created() {
    this.loading = true;

    this.availableFilters.status = this.statuses().map((s) => ({ label: `claim.status.${s}`, value: s }));
    this.availableFilters.claimType = this.claimTypes().map((type) => ({
      label: `claim.claim_type.${type}`,
      value: type,
    }));

    const [, , contractDistributionsData] = await Promise.all([
      this.fetchStores({ perPage: 2000, status: ["active", "inactive"] }),
      this.fetchOrganizations({ perPage: 2000 }),
      this.fetchContractDistributions({ save: false, perPage: 2000 }),
      this.fetchClaimsHandlers(),
    ]);

    this.setContractDistributionFilters(contractDistributionsData?.contractDistributions || []);

    this.availableFilters.stores = this.storeRecords
      .filter((store) => store.salesChannel !== null)
      .map((store) => ({ label: store.salesChannel, value: store.salesChannel }));

    this.availableFilters.organizations = this.organizationRecords.map((organization) => ({
      label: organization.name,
      value: organization.name,
    }));

    this.availableFilters.assignee = [
      { label: this.t("assignee_unassigned"), value: "unassigned" },
      ...this.claimsHandlers.map((claimsHandler) => ({
        label: claimsHandler.fullName,
        value: String(claimsHandler.id),
      })),
    ];

    const tabFromQuery = this.$route.query.tab || "open";
    const tabName = tabFromQuery === "all" ? null : tabFromQuery;
    const initialTab = this.tabs.find((t) => t.name === tabName) || this.tabs[0];
    this.tabs.forEach((t) => {
      t.current = t.name === initialTab.name;
    });
    this.updateFilter({ filterName: "ticketStatus", filterOptions: initialTab.name ? [initialTab.name] : null });

    this.syncQueryParams();
    await this.retrieveClaims();
  },
  methods: {
    ...mapActions("claims", ["fetchIndex", "updateFilter", "updateSearch", "updateOrder", "updateTicketStatus"]),
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),
    ...mapActions("contractDistributions", { fetchContractDistributions: "fetchIndex" }),
    ...mapActions("estalyManagers", ["fetchClaimsHandlers"]),

    setContractDistributionFilters(contractDistributions) {
      this.availableFilters.category = contractDistributions.map((contractDistribution) => ({
        label: contractDistribution.translatedCategory,
        value: contractDistribution.category,
      }));

      this.availableFilters.insurerContractGroupReference = [
        ...new Set(
          contractDistributions.map((contractDistribution) => contractDistribution.insurerContractGroupReference),
        ),
      ].map((reference) => ({ label: reference, value: reference }));
    },
    async retrieveClaims() {
      this.loading = true;
      const params = {
        page: this.currentPage,
        keywords: this.keywords,
        order: this.order,
        ...this.selectedFilters,
        ...(this.routeAssignee && { assignee: [this.routeAssignee] }),
      };
      await this.fetchIndex(params);
      this.loading = false;
    },
    refreshFromFirstPage() {
      if (this.currentPage !== 1) {
        this.updatePage(1);
      } else {
        this.syncQueryParams();
        this.retrieveClaims();
      }
    },
    syncQueryParams() {
      const query = { ...this.$route.query, order: this.order, tab: this.currentTabQueryParam };
      if (this.$route.query.order !== this.order || this.$route.query.tab !== this.currentTabQueryParam) {
        this.$router.replace({ query });
      }
    },
    updateFilterSearch(filterName, filterOptions) {
      this.updateFilter({
        filterName: filterName,
        filterOptions: filterOptions,
      });
      this.refreshFromFirstPage();
    },
    updateKeywordSearch(keyword) {
      this.updateSearch({ searchString: keyword });
      this.refreshFromFirstPage();
    },
    updatePage(page) {
      const query = { ...this.$route.query, page, order: this.order, tab: this.currentTabQueryParam };
      this.$router.replace({ query });
    },
    selectTab(tab) {
      this.tabs.forEach((t) => {
        t.current = t.name === tab.name;
      });
      this.updateFilterSearch("ticketStatus", tab?.name ? [tab.name] : null);
    },
    async pauseClaim({ claim, reopeningDate }) {
      this.claimToPause = null;
      await this.updateTicketStatus({
        claim: claim,
        reopeningDate: reopeningDate,
        ticketStatus: "paused",
        refresh: false,
      });
      await this.retrieveClaims();
    },
  },
};
</script>

<i18n src="./ClaimsList.i18n.json"></i18n>
